/* eslint-disable no-underscore-dangle */
import '@fullcalendar/core/vdom';

import FullCalendar from '@fullcalendar/react';
import moment from 'moment';
import React, { LegacyRef, useEffect, useMemo, useRef, useState } from 'react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import ptLocale from '@fullcalendar/core/locales/pt-br';
import { ApplicationState } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import { BulletinsState, FilterBulletinTypes } from 'store/modules/bulletins_store/types';
import {
  fetchBulletinContent,
  fetchBulletins,
  fetchBulletinsRepresented,
} from 'store/modules/bulletins_store/actions';
import Select from 'react-select';
import { captalizeFirstLetter } from 'services/captalize';
import { Link, useLocation } from 'react-router-dom';
import _ from 'underscore';
import { ProfileState } from 'store/modules/profile_store/types';
import Loader from 'components/atoms/loader';
import tabId from 'services/tabId';
import EmptyResult from 'components/atoms/empty_result';
import { Container } from './styles';

interface BulletinCalendarProps {
  currentDate: Date;
  setCurrentDate: React.Dispatch<React.SetStateAction<Date>>;
  selectedFilterId: number | null;
  setSelectedFilterId: React.Dispatch<React.SetStateAction<number | null>>;
}
interface EventTypes {
  id: string;
  title: string;
  start: string;
  className: string;
  extendedProps: {
    issue: number;
  };
}

const BulletinCalendar = (props: BulletinCalendarProps) => {
  const { currentDate, setCurrentDate, selectedFilterId, setSelectedFilterId } = props;

  const { bulletins, request } = useSelector<ApplicationState, BulletinsState>(
    (state) => state.session.bulletins,
  );
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const calendarComponentRef = useRef<LegacyRef<FullCalendar>>(null) as LegacyRef<FullCalendar>;
  const [bulletinFilters, setBulletinFilters] = useState<Array<FilterBulletinTypes> | null>(null);
  const selectedFilter: FilterBulletinTypes | null = useMemo(
    () => bulletinFilters?.find((fb) => fb?.filter?.id === selectedFilterId) || null,
    [bulletinFilters, selectedFilterId],
  );

  const [eventsList, setEventsList] = useState<Array<EventTypes>>([]);
  const [currentCompany, setCurrentCompany] = useState<number>(
    profile.currentCompany[tabId()] || profile.clientId,
  );

  const dispatch = useDispatch();

  const { search } = useLocation();
  const params = new URLSearchParams(search);

  const setCalendarMonth = (date: string) => {
    if (!calendarComponentRef) return;
    if (typeof calendarComponentRef !== 'object') return;
    const api = calendarComponentRef?.current?.getApi();
    api?.gotoDate(moment(date).format());
  };

  const handleDate = (date: string) => {
    setCalendarMonth(date);
    setCurrentDate(new Date(date));
  };

  const eventList = () => {
    if (!bulletins?.filter_bulletins) return [];

    const currentFilterBulletins = bulletins?.filter_bulletins?.find(
      (filter_bulletin) => filter_bulletin?.filter?.id === selectedFilter?.filter?.id,
    );

    if (!currentFilterBulletins) return [];

    return (
      currentFilterBulletins?.bulletin_filters
        ?.filter(
          (bulletin_filter) =>
            moment(bulletin_filter?.bulletin?.closing_date).format('MMMM') ===
            moment(currentDate).format('MMMM'),
        )
        .map((bulletin_filter) => {
          return {
            id: bulletin_filter?.id?.toString(),
            title: `Boletim ${bulletin_filter?.issue}`,
            start: bulletin_filter?.bulletin?.closing_date,
            className: `d-block calendar-cursor mx-2 ${
              bulletin_filter?.read_by === null
                ? 'bg-calendar-active'
                : bulletin_filter?.read_by
                ? 'bg-secondary'
                : 'bg-primary'
            }`,
            extendedProps: {
              issue: bulletin_filter?.issue,
            },
          };
        }) || eventsList
    );
  };

  const filterOptions = () => {
    if (!profile?.representant)
      return bulletinFilters?.map((filter_bulletin) => {
        return {
          label: filter_bulletin?.filter?.description,
          value: filter_bulletin?.filter?.id,
        };
      });
    return bulletinFilters?.map((filter_bulletin) => {
      if (profile?.currentCompany[tabId()] === filter_bulletin?.filter?.client_id)
        return {
          label: filter_bulletin?.filter?.description,
          value: filter_bulletin?.filter?.id,
        };
      return {
        label: '',
        value: 0,
      };
    });
  };

  useEffect(() => {
    if (currentCompany === profile?.clientId) dispatch(fetchBulletins());

    if (currentCompany !== profile?.clientId) dispatch(fetchBulletinsRepresented(currentCompany));
  }, [currentCompany]);

  useEffect(() => {
    setCurrentCompany(profile?.currentCompany[tabId()]);
  }, [profile]);

  const eventRender = (info: any) => {
    const issue = info?.event?.extendedProps?.issue;
    const titleContent = (
      <>
        <span className="d-none d-md-inline">Boletim </span>
        {issue}
      </>
    );
    return profile?.paralyzed ||
      (profile?.representant &&
        profile?.represented?.filter(
          (company) => company.id === profile?.currentCompany[tabId()],
        )[0]?.paralisado) ? (
      <p className={`${info?.event?.classNames[0]} text-white text-center m-0`}>{titleContent}</p>
    ) : (
      <Link
        to={
          profile?.alfa_tester
            ? `/boletim_web/public/boletins/new/${info?.event?.id}`
            : `/boletim_web/public/boletins/${info?.event?.id}`
        }
        className={`${info?.event?.classNames[0]} text-white text-center`}
      >
        {titleContent}
      </Link>
    );
  };
  useEffect(() => {
    if (!request?.fetch_bulletin_filters?.loaded) return;

    setBulletinFilters(
      _.sortBy(bulletins?.filter_bulletins, (filter_bulletin) => filter_bulletin?.filter?.id),
    );

    if (!bulletins?.filter_bulletins) return;

    setCurrentDate(new Date(bulletins?.filter_bulletins[0]?.months?.slice(-1)[0] || ''));
  }, [request?.fetch_bulletin_filters?.loaded]);

  useEffect(() => {
    if (!bulletinFilters) return;

    if (params.get('filter_id')) {
      const matched = bulletinFilters?.find(
        (filter_bulletin) => filter_bulletin?.filter?.id?.toString() === params.get('filter_id'),
      );
      setSelectedFilterId(matched?.filter?.id ?? null);
    }

    if (!params.get('filter_id')) setSelectedFilterId(bulletinFilters[0]?.filter?.id ?? null);
  }, [bulletinFilters]);

  useEffect(() => {
    if (params.get('month')) return;

    setCurrentDate(new Date(selectedFilter?.months?.slice(-1)[0] || ''));
  }, [selectedFilter]);

  useEffect(() => {
    if (request?.fetch_bulletin_content?.loading) return;
    if (request?.fetch_bulletin_content?.loaded) setEventsList(eventList());
  }, [request?.fetch_bulletin_content, bulletins.filter_bulletins]);

  useEffect(() => {
    if (selectedFilter?.filter?.client_id !== (currentCompany || profile?.clientId)) return;
    if (request?.fetch_bulletin_content?.loading) return;
    if (!currentDate?.getTime()) return;
    if (!selectedFilter) return;

    dispatch(fetchBulletinContent(selectedFilter?.filter.id || null, currentDate || null));
  }, [currentDate, selectedFilter]);

  useEffect(() => {
    setCalendarMonth(moment(currentDate).format());
  }, [request?.fetch_bulletin_content?.loaded]);

  useEffect(() => {
    if (request?.fetch_bulletin_content?.loading) return;
    if (request?.fetch_bulletin_content?.loaded) return;
    if (!request?.fetch_bulletin_content?.error) return;

    dispatch(fetchBulletinContent(selectedFilter?.filter.id || null, currentDate || null));
  }, [request?.fetch_bulletin_content]);

  if (request?.fetch_bulletin_filters?.loading || request?.fetch_bulletin_content?.loading)
    return <Loader />;

  if (request?.fetch_bulletin_content?.error)
    return <EmptyResult title="Desculpe!" description="Algo deu errado, tente novamente." />;

  return (
    <Container>
      <div className="row mb-3">
        <div className="col-12 col-md-5 col-lg-3 my-1 text-nowrap">
          <div className="mb-1">Mês selecionado</div>
          {selectedFilter?.months.map((month) => (
            <button
              className={`mr-1 btn ${
                moment(month).format('MMMM') === moment(currentDate).format('MMMM')
                  ? 'btn-primary'
                  : 'btn-outline-primary'
              }`}
              key={month}
              type="button"
              onClick={() => handleDate(month)}
            >
              {captalizeFirstLetter(moment(month).format('MMMM'))}
            </button>
          ))}
        </div>
        <div className="col-12 col-md-7 col-lg-9 my-1 ml-auto">
          {request?.fetch_bulletin_filters?.loaded &&
            bulletinFilters &&
            bulletinFilters?.length > 0 &&
            (() => {
              const visibleFilters =
                filterOptions()?.filter((filter) => filter?.label !== '') || [];

              return (
                <>
                  <div className="mb-1">Filtro selecionado</div>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <Select
                        className="filter-input"
                        value={{
                          label: selectedFilter?.filter?.description,
                          value: selectedFilter?.filter?.id,
                        }}
                        options={visibleFilters}
                        onChange={(event) => setSelectedFilterId(event?.value ?? null)}
                      />
                    </div>
                    <span className="filter-count-tag badge ml-2 px-3 py-1">
                      1 de {visibleFilters.length || bulletinFilters?.length} filtros
                    </span>
                  </div>
                </>
              );
            })()}
        </div>
      </div>
      {!profile?.representant && profile?.paralyzed && (
        <div className="row m-1">
          <div className="col-md-12 alert alert-warning">
            Prezado cliente, o seu serviço está sendo processado normalmente, porém está bloqueado
            para visualização. Para reativar o acesso ou obter mais informações entre em contato
            conosco pelo telefone (11) 3789-9102 ou pelo e-mail
            <a href="mailto:financeiro@conlicitacao.com.br">financeiro@conlicitacao.com.br</a>
          </div>
        </div>
      )}
      {profile?.representant &&
        profile?.represented?.filter(
          (company) => company.id === profile?.currentCompany[tabId()],
        )[0]?.paralisado && (
          <div className="row m-1">
            <div className="col-md-12 alert alert-warning">
              Prezado cliente, este serviço está sendo processado normalmente, porém está bloqueado
              para visualização. Entre contato com o administrador da empresa representada.
            </div>
          </div>
        )}
      {request?.fetch_bulletin_filters?.loaded && bulletinFilters?.length === 0 && (
        <div className="row">
          <div className="col-md-12 alert alert-info">
            Não existem boletins disponíveis para o filtro selecionado
          </div>
        </div>
      )}
      <div className="bulletin-calendar-wrapper w-100">
        <FullCalendar
          plugins={[dayGridPlugin, interactionPlugin]}
          locales={[ptLocale]}
          events={[
            ...eventsList,
            { daysOfWeek: [0, 6], display: 'background', backgroundColor: '#e9ecef' },
          ]}
          headerToolbar={false}
          eventContent={eventRender}
          height="auto"
          ref={calendarComponentRef}
        />
      </div>

      <div className="d-md-flex d-block align-items-center mt-3">
        <div className="d-flex align-items-center mr-5 mb-md-0 mb-2">
          <div className="legend-square bg-calendar-active mr-1 rounded mb-1" />
          Boletim não visualizado
        </div>
        <div className="d-flex align-items-center mr-5 mb-md-0 mb-2">
          <div className="legend-square bg-secondary mr-1 rounded mb-1" />
          Boletim visualizado
        </div>
        <div className="d-flex align-items-center align-items-start mb-md-0 mb-2">
          <div className="legend-square bg-primary mr-1 rounded mb-1" />
          Boletim visualizado por outro membro da equipe
        </div>
      </div>
    </Container>
  );
};

export default BulletinCalendar;
